import * as THREE from 'three';
import { basic, cloth, emis, leather, metal, std } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * El Escriba (the Chasm's Sunken Archive guardian, docs/SIMA.md «Guardianes»): «Cuando lee, pegale fuerte hasta
 * cortarlo.» A scribe of the old garrison's archive grown tall and bent over centuries of reading: a long robe of
 * cold grey stained with ink, a great hump under the hood, a long neck reaching forward to a gaunt grey face in
 * round iron spectacles. Before him, on an iron arm out of his chest like a lectern, an enormous book lies open;
 * his left hand holds its pages, his right a long black quill. Ink drips from his sleeves and hem; seals of red
 * wax hang from his belt and from the book.
 *
 * How the integrator drives him (anim/creatures/archivist.ts): 'read' (1.6 s loop) is the rule, as long as he
 * reads: the book open flat, its pages lit (the `glow` bone, built tiny, holds the light and the words rising off
 * them), his head running along the lines, his jaw chattering, the quill conducting. 'interrupt' (1.0 s) when the
 * reading is beaten down: he slams the book and reels back. Out of reading the book rests half shut.
 *
 * Bones: body (the robe, from the hips), chest, neck, head, jaw, `lectern` (the iron arm), `book`, `coverL` and
 * `coverR` (hinged at the spine), `glow` (the pages' light, built at ARCHIVIST_GLOW_REST of its size), arms
 * (arm, fore). Faces +Z.
 */

/** How small the pages' light is built (the read clip scales `glow` by 1 / ARCHIVIST_GLOW_REST). */
export const ARCHIVIST_GLOW_REST = 0.02;
export const ARCHIVIST_HEIGHT = 2.75;

export const archivist = (o?: BaseOpts & { reading?: boolean }): Model => {
  const c = begin(o, 0.95, 3301);
  const { g, rng } = c;
  const k4 = kit(rng);

  const robe = cloth('#4b545e');
  const robeD = cloth('#23282e');
  const skin = std('#a9b3bb', { roughness: 0.75 });
  const ink = std('#060609', { roughness: 0.15, metalness: 0.2 });
  const wax = std('#9a1d22', { roughness: 0.4 });
  const page = std('#b9c3cf', { roughness: 0.95, flatShading: true });
  const cover = leather('#1d2126');
  const iron = metal('#454a52', { roughness: 0.5 });
  const light = emis('#f2f7ff', '#b9d2ff', 2.2);
  const haze = basic('#cfe0ff', 0.25);

  /* ---------- the robe: to the floor, ragged and stained at the hem ---------- */
  const bodyB = joint(g, V(0, 1.0, 0), 'body');
  const body = bodyB.abs;
  {
    const geo = new THREE.CylinderGeometry(0.27, 0.56, 1.28, 14, 3, true);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const a = Math.atan2(z, x);
      const f = 1 + 0.06 * Math.sin(a * 7) * (0.64 - y);
      pos.setXYZ(i, x * f, y < -0.63 ? y + (i % 3 ? 0.05 : -0.01) : y, z * f);
    }
    geo.computeVertexNormals();
    const skirt = M(geo, robeD, body);
    skirt.position.set(0, 0.66, 0);
  }
  for (const k of [-1, 1]) {
    const p = k4.panel(body, V(k * 0.13, 1.28, 0.22), 0.3, 1.2, 0.1, 1, robe, 0.3);
    p.rotation.set(-0.24, k * 0.3, 0);
  }
  // Ink running down the hem.
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + 0.3;
    const d = M(new THREE.ConeGeometry(0.03, 0.18 + rng() * 0.2, 4), ink, body);
    d.position.set(Math.cos(a) * 0.57, 0.14, Math.sin(a) * 0.57);
    d.rotation.x = Math.PI;
  }
  const puddle = M(new THREE.CircleGeometry(0.75, 16), ink, g);
  puddle.rotation.x = -Math.PI / 2;
  puddle.position.y = 0.01;
  puddle.scale.set(1, 0.8, 1);
  // The belt, the seals and a scroll case hanging from it.
  const belt = M(new THREE.TorusGeometry(0.29, 0.03, 4, 16), cover, body);
  belt.rotation.x = Math.PI / 2;
  belt.position.y = 1.26;
  for (const [a, l] of [[0.9, 0.3], [1.5, 0.42], [2.3, 0.26]] as const) {
    const at = V(Math.cos(a) * 0.3, 1.24, Math.sin(a) * 0.3);
    limb(body, at, at.clone().add(V(0, -l, 0.02)), 0.008, 0.008, wax, 3);
    const seal = M(new THREE.CylinderGeometry(0.055, 0.055, 0.02, 9), wax, body);
    seal.position.copy(at).add(V(0, -l - 0.04, 0.03));
    seal.rotation.x = Math.PI / 2;
  }
  const tube = M(new THREE.CylinderGeometry(0.04, 0.04, 0.38, 7), cover, body);
  tube.position.set(-0.32, 1.02, -0.1);
  tube.rotation.z = 0.2;

  /* ---------- chest: bent, a great hump under the hood ---------- */
  const chestB = joint(body, V(0, 1.3, 0), 'chest');
  const chest = chestB.abs;
  k4.swell(chest, robe, 0, 1.67, 0.06, 0.34, 0.42, 0.28, 10, 7);
  k4.swell(chest, robe, 0, 2.14, -0.06, 0.42, 0.4, 0.4, 10, 7);
  k4.swell(chest, robeD, 0, 2.24, -0.2, 0.36, 0.3, 0.3, 9, 6);
  // A stole hanging forward off the shoulders, edged in sealing red.
  for (const k of [-1, 1]) {
    k4.tube(chest, [V(k * 0.16, 2.36, 0.16), V(k * 0.2, 2.02, 0.35), V(k * 0.17, 1.62, 0.32)], 0.05, robeD, 5);
    k4.tube(chest, [V(k * 0.22, 2.34, 0.16), V(k * 0.26, 2.02, 0.36), V(k * 0.23, 1.64, 0.33)], 0.012, wax, 3);
  }

  /* ---------- the neck reaching forward, the hooded head ---------- */
  const neckB = joint(chest, V(0, 2.3, 0.18), 'neck');
  const nk = neckB.abs;
  limb(nk, V(0, 2.26, 0.12), V(0, 2.34, 0.52), 0.11, 0.08, robeD, 7);
  const headB = joint(nk, V(0, 2.32, 0.5), 'head');
  const hd = headB.abs;
  // The hood: a deep cowl open at the front.
  const hood = M(new THREE.SphereGeometry(0.2, 12, 9, Math.PI * 0.32, Math.PI * 1.36), robeD, hd);
  hood.position.set(0, 2.39, 0.6);
  hood.rotation.y = Math.PI;
  hood.scale.set(1.05, 1.1, 1.05);
  // The face: long and gaunt, the nose a blade, the spectacles of iron.
  const face = M(new THREE.SphereGeometry(1, 10, 8), skin, hd);
  face.position.set(0, 2.34, 0.72);
  face.scale.set(0.1, 0.14, 0.11);
  const nose = M(new THREE.ConeGeometry(0.025, 0.14, 4), skin, hd);
  nose.position.set(0, 2.34, 0.84);
  nose.rotation.x = Math.PI / 2 + 0.5;
  for (const s of [-1, 1]) {
    const sock = M(new THREE.SphereGeometry(0.027, 6, 4), ink, hd);
    sock.position.set(s * 0.045, 2.38, 0.81);
    const rim = M(new THREE.TorusGeometry(0.038, 0.007, 4, 12), iron, hd);
    rim.position.set(s * 0.048, 2.38, 0.84);
    const lens = M(new THREE.CircleGeometry(0.034, 10), light, hd);
    lens.position.set(s * 0.048, 2.38, 0.842);
    lens.scale.setScalar(0.45);
    glowSprite(hd, '#cfe0ff', 0.1, V(s * 0.048, 2.38, 0.86), 0.7);
  }
  limb(hd, V(-0.012, 2.385, 0.845), V(0.012, 2.385, 0.845), 0.005, 0.005, iron, 3);
  // The jaw: long, the chin a point; it moves when he reads.
  const jawB = joint(hd, V(0, 2.28, 0.72), 'jaw');
  const jw = jawB.abs;
  const chin = M(new THREE.ConeGeometry(0.06, 0.16, 5), skin, jw);
  chin.position.set(0, 2.22, 0.76);
  chin.rotation.x = Math.PI + 0.45;
  const mouth = M(new THREE.BoxGeometry(0.07, 0.012, 0.02), ink, jw);
  mouth.position.set(0, 2.275, 0.825);

  /* ---------- the lectern arm and the book ---------- */
  const lecB = joint(chest, V(0, 1.67, 0.3), 'lectern');
  const lc = lecB.abs;
  M(new THREE.SphereGeometry(0.06, 7, 5), iron, lc).position.set(0, 1.67, 0.3);
  limb(lc, V(0, 1.67, 0.3), V(0, 1.58, 0.62), 0.035, 0.03, iron, 6);
  M(new THREE.SphereGeometry(0.045, 6, 4), iron, lc).position.set(0, 1.58, 0.62);
  limb(lc, V(0, 1.58, 0.62), V(0, 1.72, 0.86), 0.03, 0.03, iron, 6);
  const bkAt = V(0, 1.77, 0.9);
  const bookB = joint(lc, bkAt, 'book');
  const bk = G(bookB.abs, bkAt.x, bkAt.y, bkAt.z);
  bk.rotation.x = -0.22;
  // A cradle under the spine.
  M(new THREE.BoxGeometry(0.16, 0.04, 0.6), iron, bk).position.y = -0.04;
  const covers: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const cvB = joint(bk, V(0, 0, 0), `cover${s}`);
    const cv = cvB.abs;
    const board = M(new THREE.BoxGeometry(0.58, 0.035, 0.8), cover, cv);
    board.position.set(k * 0.3, 0, 0);
    for (const z of [-1, 1]) {
      const corner = M(new THREE.BoxGeometry(0.1, 0.045, 0.1), iron, cv);
      corner.position.set(k * 0.55, 0, z * 0.36);
    }
    // The pages: a block, bowed toward the spine, the lines of writing and a red rubric.
    const block = M(new THREE.BoxGeometry(0.52, 0.09, 0.72), page, cv);
    block.position.set(k * 0.29, 0.06, 0);
    for (let i = 0; i < 9; i++) {
      const w = 0.36 - (i % 3) * 0.05;
      const ln = M(new THREE.BoxGeometry(w, 0.004, 0.016), i === 0 ? wax : ink, cv);
      ln.position.set(k * 0.3 - (0.36 - w) / 2 * k * -1, 0.107, -0.27 + i * 0.065);
    }
    covers[`cover${s}`] = cvB.p;
  }
  // The bookmark ribbon and a seal hanging off the book.
  k4.tube(bk, [V(0.02, 0.1, 0.36), V(0.03, 0.02, 0.44), V(0.05, -0.2, 0.47), V(0.04, -0.36, 0.45)], 0.012, wax, 3);
  const bseal = M(new THREE.CylinderGeometry(0.06, 0.06, 0.02, 9), wax, bk);
  bseal.position.set(0.04, -0.4, 0.45);
  bseal.rotation.x = Math.PI / 2;
  glowSprite(bk, '#cfe0ff', 0.9, V(0, 0.2, 0), 0.25);
  // The pages' light and the words lifting off them (built tiny; the read clip brings them up).
  const glowB = joint(bk, V(0, 0.11, 0), 'glow');
  const gg = G(glowB.abs, 0, 0.11, 0);
  gg.scale.setScalar(o?.reading ? 1 : ARCHIVIST_GLOW_REST);
  for (const k of [-1, 1]) {
    const lit = M(new THREE.PlaneGeometry(0.5, 0.7), light, gg);
    lit.rotation.x = -Math.PI / 2;
    lit.position.set(k * 0.29, 0.003, 0);
  }
  for (let i = 0; i < 12; i++) {
    const a = rng() * Math.PI * 2, r = 0.1 + rng() * 0.35;
    const glyph = M(new THREE.BoxGeometry(0.05 + rng() * 0.05, 0.012, 0.03), light, gg);
    glyph.position.set(Math.cos(a) * r, 0.12 + rng() * 0.6, Math.sin(a) * r * 0.8);
    glyph.rotation.set(rng() * 0.6, rng() * 3, rng() * 0.6);
  }
  for (let i = 0; i < 5; i++) {
    const mote = M(new THREE.SphereGeometry(0.05 + rng() * 0.04, 6, 4), haze, gg);
    mote.position.set((rng() - 0.5) * 0.7, 0.2 + rng() * 0.5, (rng() - 0.5) * 0.5);
  }

  /* ---------- arms: wide sleeves, long ink-black fingers; the left on the pages, the right with the quill ---------- */
  const arms: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const armB = joint(chest, V(k * 0.36, 2.2, 0.12), `arm${s}`);
    const sh = armB.abs;
    k4.swell(sh, robe, k * 0.36, 2.18, 0.12, 0.13, 0.12, 0.13, 8, 6);
    limb(sh, V(k * 0.38, 2.16, 0.14), V(k * 0.5, 1.82, 0.36), 0.1, 0.09, robe, 7);
    const foreB = joint(sh, V(k * 0.5, 1.82, 0.36), `fore${s}`);
    const fo = foreB.abs;
    // The sleeve flaring, ink dripping from its cuff.
    const end = k < 0 ? V(-0.34, 1.84, 0.72) : V(0.42, 2, 0.62);
    limb(fo, V(k * 0.5, 1.82, 0.36), end, 0.085, 0.15, robe, 8);
    const drip = M(new THREE.ConeGeometry(0.025, 0.16, 4), ink, fo);
    drip.position.copy(end).add(V(0, -0.14, -0.02));
    drip.rotation.x = Math.PI;
    const hand = end.clone().add(k < 0 ? V(0.06, -0.03, 0.12) : V(0.03, 0.06, 0.1));
    k4.swell(fo, skin, hand.x, hand.y, hand.z, 0.04, 0.03, 0.05, 6, 4);
    for (let i = 0; i < 4; i++) {
      const tip = hand.clone().add(k < 0 ? V(0.06 + i * 0.02, -0.08, 0.06 + (i - 1.5) * 0.03) : V((i - 1.5) * 0.025, 0.1, 0.06));
      limb(fo, hand, tip, 0.011, 0.008, i % 2 ? skin : ink, 3);
    }
    if (k > 0) {
      // The quill: a long black feather and an iron nib.
      const q0 = hand.clone().add(V(0, -0.06, 0.02)), q1 = hand.clone().add(V(0.05, 0.42, -0.1));
      limb(fo, q0, q1, 0.008, 0.004, ink, 3);
      const vane = M(new THREE.ConeGeometry(0.045, 0.34, 4), ink, fo);
      vane.position.copy(q0).lerp(q1, 0.62);
      vane.quaternion.setFromUnitVectors(V(0, 1, 0), q1.clone().sub(q0).normalize());
      vane.scale.set(1, 1, 0.25);
      const nib = M(new THREE.ConeGeometry(0.01, 0.05, 4), iron, fo);
      nib.position.copy(q0).add(V(0, -0.02, 0.005));
      nib.rotation.x = Math.PI;
    }
    arms[`arm${s}`] = armB.p;
    arms[`fore${s}`] = foreB.p;
  }

  return done(c, ARCHIVIST_HEIGHT, () => undefined, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, neck: neckB.p, head: headB.p, jaw: jawB.p,
    lectern: lecB.p, book: bookB.p, coverL: covers.coverL!, coverR: covers.coverR!, glow: glowB.p,
    armL: arms.armL!, foreL: arms.foreL!, armR: arms.armR!, foreR: arms.foreR!,
  }));
};
